import { effect } from "@preact/signals";
import { authUser, setAuth, clearAuth } from "../store/auth.js";
import {
  refreshAccessToken,
  getTokenExpiry,
  RefreshTokenRequestError,
} from "./workos.js";

const REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes before expiry
const MIN_RETRY_DELAY_MS = 30_000; // Floor: never retry faster than 30s

let timer: ReturnType<typeof setTimeout> | null = null;
let refreshing = false;

function clearTimer(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

async function doRefresh(refreshToken: string): Promise<void> {
  if (refreshing) return; // prevent overlapping refreshes
  refreshing = true;
  try {
    const { accessToken, refreshToken: newRefreshToken } =
      await refreshAccessToken(refreshToken);
    const user = authUser.peek();
    if (!user) return; // logged out while refreshing
    const expiresAt = getTokenExpiry(accessToken);
    setAuth({
      userId: user.userId,
      accessToken,
      refreshToken: newRefreshToken,
      expiresAt: expiresAt ?? Date.now() + 3600_000,
    });
  } catch (error) {
    if (!(error instanceof RefreshTokenRequestError)) {
      return;
    }
    clearAuth();
  } finally {
    refreshing = false;
  }
}

/**
 * Set up a signal effect that watches `authUser` and schedules
 * a proactive token refresh 5 minutes before expiry.
 *
 * Call once at app startup. Returns a dispose function.
 */
export function setupTokenRefresh(): () => void {
  const dispose = effect(() => {
    const user = authUser.value;
    clearTimer();

    if (!user?.expiresAt) return;
    if (refreshing) return; // refresh already in flight

    const delay = user.expiresAt - Date.now() - REFRESH_BUFFER_MS;

    // Clamp to MIN_RETRY_DELAY_MS to prevent spin-loop when token
    // is already past the refresh window (delay <= 0).
    timer = setTimeout(() => {
      void doRefresh(user.refreshToken);
    }, Math.max(delay, MIN_RETRY_DELAY_MS));
  });

  return () => {
    clearTimer();
    dispose();
  };
}
